import { useEffect, useState } from 'react';

export interface AnalyticsContributor {
  id: number;
  name: string;
}
const ROLES = ['physicians', 'advancedPracticeProviders', 'nurses', 'technicians', 'anesthesiologists'] as const;
type ContributorRole = (typeof ROLES)[number];
type Contributors = Partial<Record<ContributorRole, AnalyticsContributor[]>>;
type SelectedContributors = Record<ContributorRole, number[]>;

export const retainSelectedContributors = (
  available: AnalyticsContributor[],
  known: AnalyticsContributor[],
  selected: number[]
): AnalyticsContributor[] => {
  const availableIds = new Set(available.map(person => person.id));
  const names = new Map(known.map(person => [person.id, person.name]));
  return [
    ...available,
    ...selected
      .filter(id => !availableIds.has(id))
      .map(id => ({
        id,
        name: `${names.get(id) ?? `Staff #${id}`} (no data in range)`,
      })),
  ];
};

/** Keep selected people removable even when a date/time/department filter hides their contributions. */
export const useAnalyticsContributors = (
  data: Contributors | undefined,
  selected: SelectedContributors
): Contributors => {
  const [known, setKnown] = useState<Contributors>({});
  useEffect(() => {
    if (!data) return;
    setKnown(previous =>
      Object.fromEntries(
        ROLES.map(role => [
          role,
          [...new Map([...(previous[role] ?? []), ...(data[role] ?? [])].map(person => [person.id, person])).values()],
        ])
      )
    );
  }, [data]);
  return Object.fromEntries(
    ROLES.map(role => [role, retainSelectedContributors(data?.[role] ?? [], known[role] ?? [], selected[role])])
  );
};
